/*
 * Rebuy Dynamic Bundle popup (see snippets/rebuy-bundle-modal.liquid)
 *
 * Takes over the real Rebuy Dynamic Bundle widget rendered by the app block, moves
 * that live element into a theme modal, and shows the modal when the shopper clicks
 * Add to cart on this product section.
 *
 * The widget is MOVED, never cloned: it is an app-rendered component with its own
 * listeners and state, so a clone would be dead markup. What actually moves is the
 * app block's `.shopify-block` wrapper — a theme-owned element Rebuy never replaces —
 * so re-renders inside it keep working.
 *
 * The bundle already includes the current product as its trigger item, so the theme's
 * Add to cart is cancelled outright — the bundle's own Add to cart adds everything.
 * If the widget has nothing to show (no bundle for this product/variant), the click is
 * left alone and the theme adds to cart as usual.
 *
 * Debugging: add ?rebuy-bundle-debug to the URL (or set window.RebuyBundleDebug = true)
 * to log every step to the console.
 */
(function () {
  var ATC_BUTTON = 'button[name="add"], .js-product-form-submit-btn';
  var ATC_FORM = 'form[data-type="add-to-cart-form"], form[data-type="add-to-cart-form-sticky"]';
  /* Tried in order when the configured selector finds nothing. */
  var FALLBACK_SELECTORS = ['[data-rebuy-id]', '[data-rebuy-widget-id]', '[data-rebuy-component]', '.rebuy-widget'];
  /* Rebuy's own cart/flyout widgets also match those selectors — never take those. */
  var REBUY_CART = '.rebuy-cart, .rebuy-cart__flyout, .rebuy-modal, rebuy-cart, [data-rebuy-component="cart"]';
  /* Rebuy renders one of these once a bundle is actually available. */
  var BUNDLE_CONTENT = '.rebuy-product-block, .rebuy-product-grid, .rebuy-bundle, .rebuy-widget__content';
  /* Rebuy's post-add signals — the moment to get out of the shopper's way. */
  var CART_EVENTS = ['rebuy:cart.add', 'rebuy:cart.change', 'rebuy:smartcart.show'];
  var DEBUG =
    window.RebuyBundleDebug === true || window.location.search.indexOf('rebuy-bundle-debug') !== -1;

  function log() {
    if (!DEBUG) return;
    console.log.apply(console, ['[rebuy-bundle-modal]'].concat(Array.prototype.slice.call(arguments)));
  }

  class RebuyBundleModal extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.dialog = this.querySelector('sht-dialog');
      this.mount = this.querySelector('[data-rebuy-bundle-mount]');
      this.stash = this.querySelector('[data-rebuy-bundle-stash]');
      if (!this.dialog || !this.mount || !this.stash) {
        console.warn('[rebuy-bundle-modal] markup incomplete — popup disabled');
        return;
      }

      this.sourceSelector = this.dataset.source || FALLBACK_SELECTORS[0];
      /* Only this product section's Add to cart opens the bundle — quick-view and
         other products' forms must keep their normal behaviour. */
      this.scope = document.getElementById('shopify-section-' + this.dataset.sectionId) || document;
      this.widget = null;
      this.bypass = false;

      this.claimWidget();
      /* Rebuy loads and renders asynchronously, so keep looking until it appears. */
      this.observer = new MutationObserver(this.claimWidget.bind(this));
      this.observer.observe(document.body, { childList: true, subtree: true });

      document.addEventListener('click', this.onClick.bind(this), true);
      document.addEventListener('submit', this.onSubmit.bind(this), true);

      var skip = this.querySelector('[data-rebuy-bundle-skip]');
      if (skip) skip.addEventListener('click', this.skip.bind(this));

      CART_EVENTS.forEach(
        function (name) {
          document.addEventListener(name, this.onCartEvent.bind(this));
        }.bind(this)
      );

      /* A missing widget is a setup problem (wrong template, selector, or app block
         removed) — say so once instead of failing silently. */
      setTimeout(
        function () {
          if (!this.widget) {
            console.warn(
              '[rebuy-bundle-modal] no Rebuy widget matched "' +
                this.sourceSelector +
                '" — the bundle stays inline and Add to cart is untouched.'
            );
          } else if (!this.hasBundle()) {
            log('widget claimed but still empty — Add to cart stays normal', this.widget);
          }
        }.bind(this),
        6000
      );
    }

    /* The right candidate is a Rebuy container that sits inside a theme section (the
       PDP app block) and is not part of Rebuy's cart/flyout UI. */
    findWidget() {
      var selectors = [this.sourceSelector].concat(FALLBACK_SELECTORS);

      for (var i = 0; i < selectors.length; i++) {
        var nodes = document.querySelectorAll(selectors[i]);

        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (this.contains(node)) continue; // already ours
          if (node.closest(REBUY_CART)) continue; // Smart Cart / cart recommendations
          var block = node.closest('.shopify-block') || node.closest('.shopify-section');
          if (!block) continue; // injected by Rebuy outside the theme's sections
          log('matched', selectors[i], '->', block);
          return block;
        }
      }

      return null;
    }

    /* Take the widget under our control, then promote it into the modal once Rebuy
       has rendered a bundle into it. */
    claimWidget() {
      if (this.widget && this.widget.isConnected && this.contains(this.widget)) {
        if (this.hasBundle() && this.widget.parentNode !== this.mount) {
          this.mount.appendChild(this.widget);
          if (this.observer) this.observer.disconnect();
          log('bundle rendered — moved into the modal');
        }
        return;
      }

      var found = this.findWidget();
      if (!found) return;

      var host = found.closest('.shopify-section');
      this.stash.appendChild(found);
      this.widget = found;

      /* Hide the app section only if the widget was its only content, so a shared
         `apps` section keeps rendering its other blocks. */
      if (host && !host.querySelector('.shopify-block')) {
        host.setAttribute('data-rebuy-bundle-host', '');
      }
      log('claimed widget', found, 'host section', host);
    }

    hasBundle() {
      var widget = this.widget;
      if (!widget) return false;
      if (widget.querySelector(BUNDLE_CONTENT)) return true;
      /* Class names vary by widget type — fall back to "Rebuy rendered something real". */
      return !!widget.querySelector('[class*="rebuy-"]') && widget.textContent.trim() !== '';
    }

    shouldIntercept(el) {
      if (this.bypass || !el) return false;
      if (this.contains(el)) return false; // the bundle's own controls
      if (!this.scope.contains(el)) return false;
      if (el.hasAttribute('disabled')) return false;
      if (!this.hasBundle()) {
        log('no bundle content yet — letting the theme add to cart');
        return false;
      }
      return true;
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      var button = event.target.closest(ATC_BUTTON);
      if (!button || !button.closest(ATC_FORM) || !this.shouldIntercept(button)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      log('intercepted add to cart', button);
      this.open(button);
    }

    /* Covers submits that never produce a button click (e.g. Enter in the quantity field). */
    onSubmit(event) {
      var form = event.target;
      if (!(form instanceof Element) || !form.matches(ATC_FORM) || !this.shouldIntercept(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(form.querySelector(ATC_BUTTON) || form);
    }

    open(trigger) {
      this.form = trigger.closest(ATC_FORM);
      this.submitter = trigger.matches(ATC_BUTTON) ? trigger : null;

      if (this.widget && this.widget.parentNode !== this.mount) this.mount.appendChild(this.widget);
      /* No second argument: the dialog must not run its <template> cloning pass,
         which would replace the live widget with a copy. */
      this.dialog.showModal(trigger);
    }

    close() {
      if (this.dialog.hasAttribute('hidden')) return;
      this.dialog.closeModal();
    }

    /* Opt-out: add only the product the shopper was originally buying. */
    skip() {
      var form = this.form;
      this.close();
      if (!form) return;

      this.bypass = true;
      if (form.requestSubmit) {
        form.requestSubmit(this.submitter && this.submitter.form === form ? this.submitter : undefined);
      } else if (this.submitter) {
        this.submitter.click();
      } else {
        form.submit();
      }
      this.bypass = false;
    }

    onCartEvent() {
      if (this.dialog.hasAttribute('hidden')) return;
      this.close();
      /* Let the theme's cart drawer / count re-read the cart after Rebuy's add. */
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { source: 'rebuy-bundle' } }));
    }
  }

  if (!customElements.get('rebuy-bundle-modal')) {
    customElements.define('rebuy-bundle-modal', RebuyBundleModal);
  }
})();
